/**
 * Health Story Generator
 * Transforms weekly health data into a simple, human narrative
 * Connects patterns and insights into a cohesive story (3-4 lines max)
 */

/**
 * Analyze weekly data and generate health story
 * @param {Object} weekData - Array of daily check-ins
 * @returns {Object} - Story narrative and supporting insights
 */
export const generateHealthStory = (weekData) => {
  if (!weekData || weekData.length === 0) {
    return {
      story: "Start tracking your daily habits to see your health story unfold. Every day adds a new chapter.",
      type: 'new_user',
      emoji: '📖'
    };
  }

  // Analyze patterns
  const patterns = analyzeWeeklyPatterns(weekData);
  
  // Find strongest correlation
  const mainPattern = findStrongestPattern(patterns);
  
  // Generate narrative
  const story = createNarrative(mainPattern, patterns, weekData);
  
  return {
    story: story.text,
    type: story.type,
    emoji: story.emoji,
    supportingPatterns: patterns,
    actionable: story.actionable
  };
};

/**
 * Analyze weekly patterns and correlations
 */
const analyzeWeeklyPatterns = (weekData) => {
  const patterns = [];
  
  // Pattern 1: Sleep → Energy correlation
  const poorSleepDays = weekData.filter(d => !d.sleep);
  const poorSleepLowEnergyDays = poorSleepDays.filter(d => 
    d.mood === 'tired' || d.feeling === 'tired'
  );
  
  if (poorSleepDays.length >= 2 && poorSleepLowEnergyDays.length >= 2) {
    patterns.push({
      type: 'sleep_energy',
      strength: (poorSleepLowEnergyDays.length / poorSleepDays.length) * 100,
      days: poorSleepLowEnergyDays.length,
      total: poorSleepDays.length,
      insight: 'energy drops on less sleep days'
    });
  }
  
  // Pattern 2: Hydration → Headaches correlation
  const lowHydrationDays = weekData.filter(d => !d.hydration);
  const lowHydrationHeadacheDays = lowHydrationDays.filter(d =>
    d.symptoms?.includes('headache')
  );
  
  if (lowHydrationDays.length >= 2 && lowHydrationHeadacheDays.length >= 1) {
    patterns.push({
      type: 'hydration_headache',
      strength: (lowHydrationHeadacheDays.length / lowHydrationDays.length) * 100,
      days: lowHydrationHeadacheDays.length,
      total: lowHydrationDays.length,
      insight: 'headaches appear on low hydration days'
    });
  }
  
  // Pattern 3: Movement → Stress correlation
  const lowMovementDays = weekData.filter(d => !d.activity);
  const lowMovementStressDays = lowMovementDays.filter(d =>
    d.stress_level === 'high' || d.feeling === 'stressed'
  );
  
  if (lowMovementDays.length >= 2 && lowMovementStressDays.length >= 1) {
    patterns.push({
      type: 'movement_stress',
      strength: (lowMovementStressDays.length / lowMovementDays.length) * 100,
      days: lowMovementStressDays.length,
      total: lowMovementDays.length,
      insight: 'stress is higher on days without movement'
    });
  }
  
  // Pattern 4: Good habits → Good mood (positive pattern)
  const goodHabitDays = weekData.filter(d => 
    d.sleep && d.hydration && d.activity
  );
  const goodHabitGoodMoodDays = goodHabitDays.filter(d => 
    d.mood === 'good' || d.feeling === 'good'
  );
  
  if (goodHabitDays.length >= 2 && goodHabitGoodMoodDays.length >= 1) {
    patterns.push({
      type: 'habits_mood',
      strength: (goodHabitGoodMoodDays.length / goodHabitDays.length) * 100,
      days: goodHabitGoodMoodDays.length,
      total: goodHabitDays.length,
      insight: 'you feel better when all habits align'
    });
  }
  
  // Pattern 5: Consistency trend (overall)
  const completeDays = weekData.filter(d => 
    d.sleep && d.hydration && d.activity
  ).length;
  const completionRate = (completeDays / weekData.length) * 100;
  
  if (completionRate >= 70) {
    patterns.push({
      type: 'consistency_high',
      strength: completionRate,
      days: completeDays,
      total: weekData.length,
      insight: 'you\'re building strong healthy habits'
    });
  } else if (completionRate <= 30) {
    patterns.push({
      type: 'consistency_low',
      strength: completionRate,
      days: completeDays,
      total: weekData.length,
      insight: 'consistency is the key area to focus on'
    });
  }
  
  return patterns;
};

/**
 * Find the strongest pattern to feature in the story
 */
const findStrongestPattern = (patterns) => {
  if (patterns.length === 0) return null;
  
  // Sort by strength (correlation percentage)
  const sorted = patterns.sort((a, b) => b.strength - a.strength);
  
  return sorted[0];
};

/**
 * Create narrative story from patterns
 */
const createNarrative = (mainPattern, allPatterns, weekData) => {
  if (!mainPattern) {
    // No clear patterns yet
    return {
      text: "You're building your health story. Keep tracking for a few more days to see patterns emerge.",
      type: 'building',
      emoji: '🌱',
      actionable: 'Continue your daily check-ins'
    };
  }

  // Generate story based on pattern type
  switch (mainPattern.type) {
    case 'sleep_energy':
      return {
        text: `Your energy tends to drop on days with less sleep. ${getSecondaryInsight(allPatterns, 'sleep_energy')} Getting consistent rest may help you feel more energized throughout the week.`,
        type: 'correlation',
        emoji: '😴',
        actionable: 'Try going to bed 30 minutes earlier'
      };
      
    case 'hydration_headache':
      return {
        text: `Headaches seem to appear more often when you're not drinking enough water. ${getSecondaryInsight(allPatterns, 'hydration_headache')} Improving hydration may help reduce these discomforts.`,
        type: 'correlation',
        emoji: '💧',
        actionable: 'Keep a water bottle nearby all day'
      };
      
    case 'movement_stress':
      return {
        text: `Stress feels higher on days when you haven't moved your body. ${getSecondaryInsight(allPatterns, 'movement_stress')} Adding even a short walk could help you feel more balanced.`,
        type: 'correlation',
        emoji: '🚶',
        actionable: 'Take a 10-minute walk during breaks'
      };
      
    case 'habits_mood':
      return {
        text: `You feel your best when sleep, water, and movement all come together. ${getSecondaryInsight(allPatterns, 'habits_mood')} These habits are clearly working for you — keep nurturing them.`,
        type: 'positive',
        emoji: '✨',
        actionable: 'Maintain your current routine'
      };
      
    case 'consistency_high':
      return {
        text: `You're showing up for yourself ${mainPattern.days} out of ${mainPattern.total} days this week. ${getSecondaryInsight(allPatterns, 'consistency_high')} This consistency is building a foundation for lasting health.`,
        type: 'positive',
        emoji: '🌟',
        actionable: 'Celebrate your wins, then aim for one more day next week'
      };
      
    case 'consistency_low':
      return {
        text: `Life has been busy, and tracking hasn't been consistent. ${getSecondaryInsight(allPatterns, 'consistency_low')} Even checking in 3-4 days a week can reveal helpful patterns.`,
        type: 'encouraging',
        emoji: '🌱',
        actionable: 'Set a daily reminder to check in'
      };
      
    default:
      return {
        text: `Your health data is starting to tell a story. ${weekData.length} days of tracking are showing how your habits connect to how you feel.`,
        type: 'neutral',
        emoji: '📊',
        actionable: 'Keep tracking to see clearer patterns'
      };
  }
};

/**
 * Get secondary insight to enrich the story
 */
const getSecondaryInsight = (allPatterns, excludeType) => {
  // Find another pattern to mention
  const secondary = allPatterns.find(p => p.type !== excludeType && p.strength >= 50);
  
  if (!secondary) return '';
  
  // Add connecting insight based on type
  switch (secondary.type) {
    case 'sleep_energy':
      return 'Sleep quality also seems to affect your daily energy.';
    case 'hydration_headache':
      return 'Staying hydrated could also help.';
    case 'movement_stress':
      return 'Movement also helps with stress.';
    case 'habits_mood':
      return 'When habits align, you feel noticeably better.';
    default:
      return '';
  }
};

/**
 * Generate mock weekly data (for testing/MVP)
 */
export const generateMockWeekData = () => {
  const days = [];
  const today = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Create realistic patterns
    const slept = Math.random() > 0.3;
    const hydrated = Math.random() > 0.4;
    const moved = Math.random() > 0.35;
    
    // Correlate mood with habits
    let mood = 'okay';
    let feeling = 'okay';
    if (slept && hydrated) {
      mood = Math.random() > 0.3 ? 'good' : 'okay';
      feeling = 'good';
    } else if (!slept) {
      mood = 'tired';
      feeling = 'tired';
    }
    
    // Correlate symptoms with habits
    const symptoms = [];
    if (!hydrated && Math.random() > 0.5) {
      symptoms.push('headache');
    }
    
    const stress_level = !moved && Math.random() > 0.5 ? 'high' : 'moderate';
    
    days.push({
      date: date.toISOString().split('T')[0],
      sleep: slept,
      hydration: hydrated,
      activity: moved,
      mood,
      feeling,
      symptoms,
      stress_level
    });
  }
  
  return days;
};

/**
 * Main export function that handles real or mock data
 */
export const createHealthStory = (realData = null) => {
  // Use real data if provided, otherwise generate mock
  const weekData = realData || generateMockWeekData();
  
  return generateHealthStory(weekData);
};

export default {
  generateHealthStory,
  createHealthStory,
  generateMockWeekData
};
